import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {AbstractControl, FormBuilder, Validators} from '@angular/forms';
import {BmpDrawing} from '@domain/production-schedule/bmp-drawing';
import {Drawing} from '@domain/production-schedule/drawing';
import {DrawingConfigurationWithColorAndYarnSet} from '@domain/production-schedule/drawing-configuration-with-color-and-yarn-set';
import {DrawingImage} from '@domain/production-schedule/drawing-image';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {EpDrawing} from '@domain/production-schedule/ep-drawing';
import {TftDrawing} from '@domain/production-schedule/tft-drawing';
import {Permission} from '@domain/profile/permission.enum';
import {Authentication, AUTHENTICATION} from '@infrastructure/http/authentication/authentication';
import {DRAWINGS, Drawings} from '@infrastructure/http/drawing/drawings';
import {ListDrawingConfiguration} from '@presentation/components/drawing-list/list-drawing-configuration';
import {OverviewListArticleForOrderLine} from '@presentation/pages/texfab/orderbook/overview/overview-list-article-for-order-line';
import {AssertionUtils, canShowErrorForErrorCodeAndFormControlForFormGroup, convertToCommercialUnitMillimeter, TranslateService, Unit} from '@vdw/angular-component-library';
import {isEqual} from 'lodash-es';
import {DrawingConfigurationForEpDrawing} from '../../drawing-configuration-for-ep-drawing';
import {BaseDrawingFormComponent} from '../base-drawing-form.component';
import {ConfigurationForm, DrawingForm} from '../drawing-form';

@Component({
  selector: 'app-edit-drawing-form',
  templateUrl: './edit-drawing-form.component.html',
  styleUrls: ['../base-drawing-form.component.scss']
})
export class EditDrawingFormComponent extends BaseDrawingFormComponent implements OnChanges, AfterViewInit {
  @Input() public parameterFieldsDisabled: boolean;
  @Input() public disableAllFields: boolean;
  @Input() public scrollToConfiguration: boolean = false;

  @Output() public saveAsEp: EventEmitter<DrawingConfigurationForEpDrawing> = new EventEmitter<DrawingConfigurationForEpDrawing>();
  @ViewChild('targetSection') public targetSection: ElementRef;
  public requiredPermissionToSaveAsEp: Permission = Permission.TEXEDIT_PROC;
  public texeditWritePermission: Permission = Permission.TEXEDIT_EDIT;

  public constructor(
    @Inject(DRAWINGS) protected drawingsService: Drawings,
    formBuilder: FormBuilder,
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    super(drawingsService, formBuilder, translate);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
    const isDrawingTypeBmp = this.isDrawingTypeBmp();

    if (changes.drawing?.currentValue !== changes.drawing?.previousValue) {
      if (AssertionUtils.isNullOrUndefined(this.drawingForm)) {
        this.setFormFields();

        if (isDrawingTypeBmp) {
          this.setFormFieldsForBmpDrawing();
        }
      }

      this.setValuesForFormFields();

      if (isDrawingTypeBmp) {
        this.setValuesForBmpDrawingFormFields(this.disableAllFields);
      } else {
        this.drawingForm.controls.shape.disable();
      }
    }

    if (!this.canEditInputs()) {
      this.drawingForm.disable();
    }

    if (this.parameterFieldsDisabled) {
      this.disableDrawingFormParameterFields();
    }

    this.cdr.detectChanges();
  }

  public ngAfterViewInit(): void {
    this.scrollToSection();
  }

  public scrollToSection(): void {
    if (!AssertionUtils.isNullOrUndefined(this.targetSection) && this.scrollToConfiguration) {
      this.targetSection.nativeElement.scrollIntoView({behavior: 'smooth'});
    }
  }

  public isValid(): boolean {
    if (this.parameterFieldsDisabled) {
      return this.drawingForm.controls['configurations'].valid;
    } else {
      return this.drawingForm.valid;
    }
  }

  public canShowConfiguration(configurationForm: AbstractControl): boolean {
    if (this.productionScheduleDrawing instanceof OverviewListArticleForOrderLine) {
      if (this.productionScheduleDrawing.colorSet.id === configurationForm.value?.colorSet?.id && this.productionScheduleDrawing.machineQuality.id === configurationForm.value?.quality?.id) {
        if (!AssertionUtils.isNullOrUndefined(this.productionScheduleDrawing.coloredYarnSet?.overviewListYarnSet)) {
          return this.productionScheduleDrawing.coloredYarnSet?.overviewListYarnSet === configurationForm.value?.yarnSet?.id;
        } else {
          return true;
        }
      } else {
        return false;
      }
    } else if (this.productionScheduleDrawing instanceof ListDrawingConfiguration) {
      return this.canShowConfigurationForListDrawingConfiguration(configurationForm);
    } else {
      return true;
    }
  }

  public createDrawingFromForm(): Drawing {
    const drawingForm: DrawingForm = this.drawingForm;

    let updatedDrawing: Drawing = null;

    if (isEqual(this.drawing.drawingType, DrawingType.BMP)) {
      updatedDrawing = this.createBmpDrawingFromForm(drawingForm);
    } else if (isEqual(this.drawing.drawingType, DrawingType.EP)) {
      updatedDrawing = this.createEpDrawingFromForm(drawingForm);
    } else {
      updatedDrawing = this.createTftDrawingFromForm(drawingForm);
    }

    return updatedDrawing;
  }

  public addConfigurationToDrawing(): void {
    this.drawingForm.controls.configurations.push(
      this.formBuilder.group({
        quality: this.formBuilder.control(null, Validators.required),
        colorSet: this.formBuilder.control(null),
        yarnSet: this.formBuilder.control(null),
        mainColorsCreelPosition: this.formBuilder.control(null),
        borderColorsCreelPosition: this.formBuilder.control(null),
        image: this.formBuilder.control({id: this.drawing.image.id, data: this.drawing.image.data} as DrawingImage, Validators.required),
        heightInCm: this.formBuilder.control(0, Validators.min(0)),
        widthInCm: this.formBuilder.control(0, Validators.min(0))
      }) as ConfigurationForm
    );
  }

  public canShowErrorForFormControl(errorCode: string, formControlName: string): boolean {
    return canShowErrorForErrorCodeAndFormControlForFormGroup(errorCode, formControlName, this.drawingForm);
  }

  public canExpand(): boolean {
    return this.drawingForm.controls.configurations.length > 0;
  }

  public changeDrawingImage(image: DrawingImage): void {
    this.drawing.image = image;
    this.setValuesForFormFields();
  }

  public imageDownloaded(image: string): void {
    const drawingImage = this.getDrawingImage();
    drawingImage.data = image;
    this.drawingForm.controls.drawingImage.setValue(drawingImage);
  }

  public isDrawingTypeBmp(): boolean {
    return isEqual(this.drawing.drawingType, DrawingType.BMP);
  }

  public onSaveAsEp(drawingConfigurationForEpDrawing: DrawingConfigurationForEpDrawing = null): void {
    this.saveAsEp.emit(drawingConfigurationForEpDrawing);
  }

  public isDrawingTypeEp(): boolean {
    return isEqual(this.drawing.drawingType, DrawingType.EP);
  }

  public canEditInputs(): boolean {
    return this.currentSubscription?.hasPermission(this.texeditWritePermission);
  }

  private canShowConfigurationForListDrawingConfiguration(configurationForm: AbstractControl): boolean {
    const productionScheduleDrawing = this.productionScheduleDrawing as ListDrawingConfiguration;
    if (productionScheduleDrawing.colorSet.id === configurationForm.value?.colorSet?.id && productionScheduleDrawing.quality.id === configurationForm.value?.quality?.id) {
      if (!AssertionUtils.isNullOrUndefined(productionScheduleDrawing.yarnSet)) {
        return productionScheduleDrawing.yarnSet?.id === configurationForm.value?.yarnSet?.id;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  private createBmpDrawingFromForm(drawingForm: DrawingForm): BmpDrawing {
    const configurations: DrawingConfigurationWithColorAndYarnSet[] = drawingForm.controls.configurations.value.map((configuration: any) => {
      return new DrawingConfigurationWithColorAndYarnSet(
        configuration.image,
        configuration.mainColorsCreelPosition,
        configuration.borderColorsCreelPosition,
        configuration.colorSet,
        configuration.quality,
        {
          heightInMM: convertToCommercialUnitMillimeter({
            unit: Unit.CENTIMETER,
            value: configuration.heightInCm
          }),
          widthInMM: convertToCommercialUnitMillimeter({
            unit: Unit.CENTIMETER,
            value: configuration.widthInCm
          })
        },
        configuration.yarnSet
      );
    });

    return new BmpDrawing(
      this.drawing.id,
      drawingForm.getRawValue().name,
      drawingForm.getRawValue().drawingImage,
      {
        heightInPx: drawingForm.getRawValue().heightInPx,
        widthInPx: drawingForm.getRawValue().widthInPx
      },
      null,
      drawingForm.getRawValue().shape,
      this.drawing.used,
      this.drawing.repeated,
      [],
      drawingForm.getRawValue().code,
      configurations,
      drawingForm.getRawValue().threadByThread
    );
  }

  private createEpDrawingFromForm(drawingForm: DrawingForm): EpDrawing {
    return new EpDrawing(this.drawing.id, drawingForm.value.name, drawingForm.value.drawingImage, null, null, this.drawing.shape, false, this.drawing.repeated, null);
  }

  private createTftDrawingFromForm(drawingForm: DrawingForm): TftDrawing {
    return new TftDrawing(this.drawing.id, drawingForm.value.name, drawingForm.value.drawingImage, this.drawing.shape, this.drawing.repeated);
  }

  private disableDrawingFormParameterFields(): void {
    this.drawingForm.controls.name.disable();
    this.drawingForm.controls.drawingImage.disable();
    this.drawingForm.controls.shape.disable();
    this.drawingForm.controls.code.disable();
    this.drawingForm.controls.heightInPx.disable();
    this.drawingForm.controls.widthInPx.disable();
    this.drawingForm.controls.threadByThread.disable();
  }
}
